import { OverlayContext, OverlayOwnerContext } from "@/components/modal/overlay-context";
import { lockDocumentScroll } from "@/components/modal/overlay-scroll-lock";
import type { OverlayEntry } from "@/components/modal/overlay.types";
import { useContext, useEffect, useSyncExternalStore } from "react";

/** Keep owned floating content inside its layer's inertness and stacking boundary. */
export function useOverlayPortalRoot() {
  const context = useContext(OverlayContext);
  const ownerId = useContext(OverlayOwnerContext);
  const entries = useSyncExternalStore(
    context?.manager.subscribe ?? emptySubscribe,
    context?.manager.getSnapshot ?? emptySnapshot,
    emptySnapshot,
  );
  return (
    entries
      .find((entry) => entry.id === ownerId)
      ?.element.querySelector<HTMLElement>("[data-overlay-floating-root]") ?? null
  );
}

/** Null while an owner's layer is mounting; otherwise its host or the page body. */
export function useOverlayPortalTarget() {
  const ownerId = useContext(OverlayOwnerContext);
  const ownerRoot = useOverlayPortalRoot();
  return ownerId != null ? ownerRoot : typeof document === "undefined" ? null : document.body;
}

export function useOverlayRoot() {
  return useContext(OverlayContext)?.root ?? null;
}

export function useOverlayBackgroundScrollLock(locked: boolean) {
  const manager = useContext(OverlayContext)?.manager;
  useEffect(() => {
    if (locked) return manager ? manager.acquireScrollLock() : lockDocumentScroll();
  }, [manager, locked]);
}

export function useOverlayManager() {
  return useContext(OverlayContext)?.manager ?? null;
}

const EMPTY_ENTRIES: readonly OverlayEntry[] = [];
const emptySnapshot = () => EMPTY_ENTRIES;
const emptySubscribe = () => () => {};

export function useOverlayIsTop(id: string) {
  const context = useContext(OverlayContext);
  const entries = useSyncExternalStore(
    context?.manager.subscribe ?? emptySubscribe,
    context?.manager.getSnapshot ?? emptySnapshot,
    emptySnapshot,
  );
  return !context || entries.at(-1)?.id === id;
}
